import { useMemo, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, ReferenceLine } from "recharts";
import { addMonths, format } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { Slider } from "@/components/ui/slider";
import { useCollection } from "@/hooks/useCollection";
import { rupiah } from "@/lib/format";
import { Section } from "./shared";
import { MascotBubble } from "@/components/Mascot";

function simulate(target, current, monthly, rate) {
  const r = rate / 100 / 12;
  let bal = current, m = 0;
  const pts = [{ m: 0, bal }];
  while (bal < target && m < 600) {
    bal = bal * (1 + r) + monthly;
    m++;
    pts.push({ m, bal: Math.round(bal) });
  }
  return { months: bal >= target ? m : null, pts };
}

const Num = ({ label, value, onChange, testid }) => (
  <label className="text-xs font-medium text-pink-600 space-y-1">
    <span>{label}</span>
    <input className="field" type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} />
  </label>
);

export default function SimulatorTab() {
  const { items: goals } = useCollection("goals");
  const [target, setTarget] = useState(10000000);
  const [current, setCurrent] = useState(0);
  const [monthly, setMonthly] = useState(1000000);
  const [rate, setRate] = useState(3);
  const { months, pts } = useMemo(() => simulate(target, current, monthly, rate), [target, current, monthly, rate]);
  const step = Math.max(1, Math.ceil(pts.length / 24));
  const data = pts.filter((_, i) => i % step === 0 || i === pts.length - 1).map((p) => ({ ...p, label: format(addMonths(new Date(), p.m), "MMM yy", { locale: localeId }) }));

  return (
    <div className="space-y-4" data-testid="duitku-simulator">
      {goals.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {goals.map((g) => (
            <button key={g.id} className="chip chip-off" data-testid="simulator-pick-goal" onClick={() => { setTarget(g.target); setCurrent(g.saved); }}>{g.name}</button>
          ))}
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-4">
        <Section title="Atur simulasi nabung">
          <div className="space-y-4">
            <Num label="Target (Rp)" value={target} onChange={setTarget} testid="simulator-target" />
            <Num label="Sudah terkumpul (Rp)" value={current} onChange={setCurrent} testid="simulator-current" />
            <div>
              <div className="flex justify-between text-xs font-medium text-pink-600 mb-2"><span>Nabung per bulan</span><span data-testid="simulator-monthly-value">{rupiah(monthly)}</span></div>
              <Slider value={[monthly]} min={50000} max={20000000} step={50000} onValueChange={([v]) => setMonthly(v)} data-testid="savings-simulator-slider" />
            </div>
            <Num label="Bunga per tahun (%)" value={rate} onChange={setRate} testid="simulator-rate" />
          </div>
        </Section>
        <Section title="Kapan tercapai?" className="lg:col-span-2">
          <p className="font-heading text-3xl font-semibold text-pink-500" data-testid="simulator-result">
            {months === null ? "Belum tercapai dalam 50 tahun" : months === 0 ? "Sudah tercapai!" : `${Math.floor(months / 12) ? `${Math.floor(months / 12)} thn ` : ""}${months % 12} bln`}
          </p>
          {months > 0 && <p className="text-sm text-gray-500">Perkiraan tercapai {format(addMonths(new Date(), months), "MMMM yyyy", { locale: localeId })}</p>}
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F472B6" stopOpacity={0.5} /><stop offset="100%" stopColor="#F472B6" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 16, border: "1px solid #FBCFE8" }} />
                <ReferenceLine y={target} stroke="#38BDF8" strokeDasharray="4 4" />
                <Area dataKey="bal" name="Saldo" stroke="#EC4899" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>
      <MascotBubble pose="happy">Tips: naikkan setoran sedikit saja tiap bulan, lihat betapa cepat targetmu tercapai!</MascotBubble>
    </div>
  );
}
